import { isEqual, omit, uniq } from 'lodash-es';

import type { SavedSearchItem } from '@iconik-io/iconik-api/src/schema_patched/saved_search';
import {
  httpGetSearchSavedBySearchId,
  httpGetSearchSavedBySearchIdContentInfo,
  httpPostSearch,
  httpPostSearchContentInfo,
  type SearchSearchCriteriaSchema,
} from '@iconik-io/iconik-api/src/services/search';
import {
  OBJECT_TYPE_SAVED_SEARCHES,
  type ObjectType,
  type SearchObjectType,
} from '@iconik-io/iconik-api/src/types/object_types';
import type { SearchItemEntity } from '@iconik-io/shared-lib/src/redux/entities/normalized/search_item';
import { groupByObjectType } from '@iconik-io/shared-lib/src/utils/group_by_object_type';
import { filterValuesToCriteriaFilter } from '@iconik-io/shared-lib/src/utils/search/get_filter_values_to_criteria_filter';
import { prepareQueryForMainSearch } from '@iconik-io/shared-lib/src/utils/search/prepare_query_for_main_search';
import { toSortOrder } from '@iconik-io/shared-lib/src/utils/sort/types';

import { selectVisibleSearchFieldNames } from '../../components/search_items/selectors';
import { selectIsCurrentSortCustom } from '../../components/search_settings/selectors';
import { CUSTOM_ORDER } from '../../components/search_settings/sorting_selector/constants';
import { SHARE_SORTING_SETTINGS_KEY } from '../../constants/local_storage';
import {
  COLLECTION_PAGE_ROUTE,
  FAVOURITES_PAGE_ROUTE,
  SAVED_SEARCH_PAGE_ROUTE,
  SHARE_COLLECTION_PAGE_ROUTE,
} from '../../constants/routes';
import { loadSortingSettings } from '../../middleware/sorting_settings_manager';
import type { AppDispatch, AppState, GetState } from '../../store/types';
import { formatAssetCount } from '../../utils/format_asset_count';
import { criteriaToFilterValues } from '../../utils/search_utils/criteria_to_filter_values';
import { historyReplace } from '../history/actions';
import { searchActions } from '../pages/search/actions';
import {
  DEFAULT_SEARCH_FIELDS,
  DEFAULT_SEARCH_REQUEST_FIELDS,
  DEFAULT_SEARCH_STATUS_VALUE,
  INITIAL_SEARCH_SORTING,
  INITIAL_SHARE_SORTING,
} from '../pages/search/constants';
import {
  criteriaToFacetValues,
  filtersObjectToElastic,
  getRouteCollectionId,
} from '../pages/search/utils';
import { SEARCH_QUERY_FILTER_KEY } from './active_filter_chips';
import { selectSearchFields } from './selectors';

export const defaultDocTypes: SearchSearchCriteriaSchema['doc_types'] = [
  'assets',
  'collections',
];

const activeStatusTerm = {
  name: 'status',
  value_in: ['ACTIVE'],
};

const defaultActiveTerms = [activeStatusTerm];

const applyFacetsAndFilters = (body: SearchSearchCriteriaSchema) => {
  const newBody = structuredClone(body);

  const facetFiltersLength = body.facets_filters?.length ?? 0;
  const filterFiltersLength = body.filter?.filters?.length ?? 0;

  if (
    !body.query &&
    facetFiltersLength === 0 &&
    filterFiltersLength === 0 &&
    isEqual(body.filter?.terms, defaultActiveTerms)
  ) {
    if (!newBody.filter) {
      newBody.filter = {
        operator: 'AND',
      };
    }
    if (!newBody.filter.filters) {
      newBody.filter.filters = [];
    }
    newBody.filter.filters.push({
      operator: 'OR',
      terms: [
        {
          name: 'is_root',
          value: 'true',
        },
        {
          name: 'is_root',
          missing: true,
        },
      ],
    });
  }
  return newBody;
};

export const getSavedSearchRequestBody = (
  savedSearchCriteria: SearchSearchCriteriaSchema,
  multiSelectFieldNames?: ReadonlySet<string>,
) => {
  const filterValuesState = criteriaToFilterValues(
    savedSearchCriteria,
    undefined,
    multiSelectFieldNames,
  );
  const { unsupportedFilters } = filterValuesState;
  let { filterValues } = filterValuesState;
  const facetValues = criteriaToFacetValues(
    savedSearchCriteria.facets_filters ?? [],
  );
  const { query } = savedSearchCriteria;
  const includeFields = savedSearchCriteria.include_fields
    ? savedSearchCriteria.include_fields
    : uniq(DEFAULT_SEARCH_REQUEST_FIELDS);
  let sortOptions;

  if (savedSearchCriteria.sort) {
    sortOptions = savedSearchCriteria.sort.map((sortItem) => ({
      name: sortItem.name,
      order: toSortOrder(sortItem.order),
    }));
  }

  filterValues = {
    ...filterValues,
    status: filterValues.status ?? ['ACTIVE'],
  };

  const filter = filterValuesToCriteriaFilter(filterValues, unsupportedFilters);

  let requestBody: SearchSearchCriteriaSchema = {
    doc_types: defaultDocTypes,
    facets: savedSearchCriteria.facets,
    include_fields: includeFields,
    sort: sortOptions,
    query: prepareQueryForMainSearch(query),
    filter,
    facets_filters: filtersObjectToElastic(facetValues),
  };

  requestBody.search_fields = DEFAULT_SEARCH_FIELDS;

  requestBody = applyFacetsAndFilters(requestBody);

  return requestBody;
};

export const preprocessSortSettings =
  () =>
  (dispatch: AppDispatch, getState: GetState): void => {
    const state = getState();
    const isCurrentSortCustom = selectIsCurrentSortCustom(state);
    const route = state.routeParams.route;
    const entityId = state.routeParams.params.id;
    const collection = state.entities.collections[entityId];
    const savedSearch = state.entities.saved_search_results[entityId];
    const savedSearchSort =
      savedSearch?.search_criteria_document?.criteria.sort ?? [];
    if (isCurrentSortCustom) {
      const isSortSourceRoute = [
        COLLECTION_PAGE_ROUTE,
        SHARE_COLLECTION_PAGE_ROUTE,
        SAVED_SEARCH_PAGE_ROUTE,
      ].includes(route);
      const isSortPreSet =
        collection?.custom_order_status === 'ENABLED' ||
        savedSearchSort.length > 0;
      if (!isSortSourceRoute || !isSortPreSet) {
        dispatch(searchActions.setSort(INITIAL_SEARCH_SORTING));
      }
    }

    if (state.routeParams.route === SHARE_COLLECTION_PAGE_ROUTE) {
      const storedSortingSettings = loadSortingSettings(
        sessionStorage,
        SHARE_SORTING_SETTINGS_KEY,
      );
      if (storedSortingSettings) {
        dispatch(searchActions.setSort(storedSortingSettings));
      } else if (collection?.custom_order_status === 'ENABLED') {
        dispatch(searchActions.setSort(CUSTOM_ORDER));
      } else {
        dispatch(searchActions.setSort(INITIAL_SHARE_SORTING));
      }
    }
  };

type SearchDocumentType = 'assets' | 'collections';

function getSearchItemCount(
  items: (SearchItemEntity | SavedSearchItem)[],
  objectType: SearchObjectType,
) {
  const groupedItems = groupByObjectType(['collections', 'assets'], items);
  const collectionIds = groupedItems.get('collections')?.map(({ id }) => id);
  const assetIds =
    objectType === 'assets'
      ? groupedItems.get('assets')?.map(({ id }) => id)
      : [];
  const docTypes: SearchDocumentType[] =
    objectType === 'assets' ? ['assets'] : ['collections'];
  const searchDocument: SearchSearchCriteriaSchema = {
    doc_types: docTypes,
    include_fields: ['id'],
    filter: {
      filters: [
        {
          operator: 'NOT',
          terms: [
            {
              name: 'status',
              value: 'DELETED',
            },
          ],
        },
        {
          operator: 'OR',
          terms: [
            {
              name: 'ancestor_collections',
              value_in: collectionIds ?? [],
            },
            {
              name: 'id',
              value_in: assetIds ?? [],
            },
          ],
        },
      ],
      operator: 'AND',
    },
    sort: [
      {
        name: 'date_created',
        order: 'desc',
      },
    ],
  };
  return httpPostSearch(searchDocument, {
    per_page: 0,
    save_search_history: false,
  });
}

export const getAssetCount = (items: (SearchItemEntity | SavedSearchItem)[]) =>
  getSearchItemCount(items, 'assets');

export function getAssetCountText(
  items: (SearchItemEntity | SavedSearchItem)[],
) {
  return (dispatch: AppDispatch) =>
    dispatch(getAssetCount(items)).then((response) =>
      formatAssetCount(response.body.total),
    );
}

export const getSavedSearchItemCount =
  (id: string) => (dispatch: AppDispatch) =>
    dispatch(httpGetSearchSavedBySearchId(id, { per_page: 0 })).then(
      (response) => response.body.total,
    );

const contentInfoAssetCountText = (assetCount?: number | null) =>
  assetCount ? `${assetCount} ${assetCount > 1 ? 'assets' : 'asset'}` : '';

export const getSavedSearchAssetCountText =
  (id: string) => (dispatch: AppDispatch) =>
    dispatch(httpGetSearchSavedBySearchIdContentInfo(id)).then(({ body }) =>
      contentInfoAssetCountText(body.assets_count),
    );

export const getItemsCountText =
  (
    items: (SearchItemEntity | SavedSearchItem)[],
    containerObjectType?: ObjectType,
  ) =>
  (dispatch: AppDispatch): Promise<string | undefined> => {
    if (containerObjectType) {
      const item = items[0];
      if (containerObjectType === OBJECT_TYPE_SAVED_SEARCHES && item) {
        return dispatch(getSavedSearchItemCount(item.id)).then((itemCount) =>
          itemCount
            ? ` - ${itemCount} ${itemCount > 1 ? 'assets' : 'asset'}`
            : '',
        );
      }
      return dispatch(getAssetCountText(items));
    }
    if (items.length > 1) {
      return Promise.resolve(` - ${items.length} items`);
    }
    return Promise.resolve(undefined);
  };

export const getSearchRequestBody = (state: AppState) => {
  const pageState = state.pages.search.list;
  const { facetValues, query, unsupportedFilters, rawCriteria } =
    state.panelsState.searchFilters;
  const disabledFilters = state.panelsState.searchFilters.disabledFilters ?? [];

  if (rawCriteria) {
    return {
      doc_types: defaultDocTypes,
      ...rawCriteria,
      sort: pageState.sort,
    };
  }

  let { filterValues } = state.panelsState.searchFilters;
  const visibleFields = selectVisibleSearchFieldNames(state);

  const userId = state.authReducer.userData.id;

  filterValues = {
    ...filterValues,
    status: filterValues.status ?? DEFAULT_SEARCH_STATUS_VALUE,
  };

  const hasCollectionTerm =
    Boolean(filterValues.in_collections) ||
    Boolean(filterValues.ancestor_collections);
  const collectionId = getRouteCollectionId(state);

  if (state.routeParams.route === FAVOURITES_PAGE_ROUTE) {
    filterValues = {
      ...filterValues,
      favoured_by: [userId],
    };
  }

  if (collectionId && !hasCollectionTerm) {
    filterValues = {
      ...filterValues,
      in_collections: [collectionId],
    };
  }

  // Disabled filters stay in Redux but never reach the request.
  const activeFilterValues = disabledFilters.length
    ? omit(filterValues, disabledFilters)
    : filterValues;
  const activeFacetValues = disabledFilters.length
    ? omit(facetValues, disabledFilters)
    : facetValues;
  const activeQuery = disabledFilters.includes(SEARCH_QUERY_FILTER_KEY)
    ? ''
    : query;

  const filter = filterValuesToCriteriaFilter(
    activeFilterValues,
    unsupportedFilters,
  );

  let requestBody: SearchSearchCriteriaSchema = {
    doc_types: defaultDocTypes,
    facets: state.settings.merged.facet_fields?.map(({ name }) => name),
    include_fields: uniq([...DEFAULT_SEARCH_REQUEST_FIELDS, ...visibleFields]),
    sort: pageState.sort,
    query: prepareQueryForMainSearch(activeQuery),
    filter,
    facets_filters: filtersObjectToElastic(activeFacetValues),
  };

  requestBody.search_fields = selectSearchFields(state);

  requestBody = applyFacetsAndFilters(requestBody);

  return requestBody;
};

export const getSearchResultsAssetCountText =
  () => (dispatch: AppDispatch, getState: GetState) => {
    const searchBody = getSearchRequestBody(getState());
    return dispatch(
      httpPostSearchContentInfo({ ...searchBody, doc_types: ['assets'] }),
    ).then(({ body }) => contentInfoAssetCountText(body.assets_count));
  };

export const searchObjectsByIds =
  (
    ids: string[],
    docTypes?: SearchSearchCriteriaSchema['doc_types'],
    includeFields?: string[],
  ) =>
  (dispatch: AppDispatch) => {
    const searchCriteria = {
      doc_types: docTypes,
      include_fields: includeFields,
      filter: {
        operator: 'AND',
        terms: [
          {
            name: 'id',
            value_in: ids,
          },
        ],
      },
    };
    return dispatch(
      httpPostSearch(searchCriteria, { save_search_history: false }),
    );
  };

/**
 * Writes a scoped search term into the current route's `?search=`, replacing
 * rather than pushing so that typing does not fill the back stack. `page` is
 * dropped because a filtered view is not paginated.
 *
 * @see SearchScope
 */
export const replaceUrlSearchTerm =
  (term: string) =>
  (dispatch: AppDispatch, getState: GetState): void => {
    const { pathname, search } = getState().router.location;
    const currentSearch = new URLSearchParams(search).toString();
    const urlParams = new URLSearchParams(search);
    if (term) {
      urlParams.set('search', term);
    } else {
      urlParams.delete('search');
    }
    urlParams.delete('page');
    const nextSearch = urlParams.toString();
    if (nextSearch === currentSearch) {
      return;
    }
    dispatch(
      historyReplace(`${pathname}${nextSearch ? `?${nextSearch}` : ''}`),
    );
  };
